/**
 * Callable members exposed to UserControl ScopeScript at runtime.
 */

import { isValidJsIdentifier, parseScopeScriptParameterList } from './controlDesignerScopeScript.js';

/** Matches utils.apiWrapper passed as the `api` ScopeScript parameter. */
export const SCOPE_SCRIPT_API_MEMBERS = [
    { name: 'get', kind: 'function', runtimeSource: 'api' },
    { name: 'post', kind: 'function', runtimeSource: 'api' },
    { name: 'put', kind: 'function', runtimeSource: 'api' },
    { name: 'apiRequest', kind: 'function', runtimeSource: 'api' },
];

/**
 * @param {Record<string, unknown>|null|undefined} obj
 * @param {string} runtimeSource
 * @returns {{ name: string, kind: 'function', runtimeSource: string }[]}
 */
export function listCallableMembersFromObject(obj, runtimeSource) {
    if (!obj || typeof obj !== 'object')
        return [];

    return Object.keys(obj)
        .filter((name) => isValidJsIdentifier(name) && typeof obj[name] === 'function')
        .map((name) => ({
            name,
            kind: 'function',
            runtimeSource,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * @returns {{ name: string, kind: 'function', runtimeSource: 'api' }[]}
 */
export function buildApiDotCompletions() {
    return [...SCOPE_SCRIPT_API_MEMBERS].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Extract formal parameter names from a runtime function value.
 * @param {unknown} fn
 * @returns {string[]}
 */
export function parseFunctionParameterNames(fn) {
    if (typeof fn !== 'function')
        return [];

    try {
        const source = Function.prototype.toString.call(fn).trim();

        let match = /^(?:async\s+)?function(?:\s+[A-Za-z_$][\w$]*)?\s*\(([^)]*)\)/.exec(source);
        if (match)
            return parseScopeScriptParameterList(match[1]);

        match = /^(?:async\s+)?\(([^)]*)\)\s*=>/.exec(source);
        if (match)
            return parseScopeScriptParameterList(match[1]);

        match = /^(?:async\s+)?([A-Za-z_$][\w$]*)\s*=>/.exec(source);
        if (match)
            return [match[1]];

        return [];
    }
    catch {
        return [];
    }
}
